import { UserRoleEnum } from '@enum/role.enum';
import { SideNavigationMenuType } from '@type/side-navigation-menu.type';

import { ALL_PERMISSIONS } from '@models/permission.model';

/**
 * Top-level navigation menu items.
 * Filtered at runtime by user role.
 *
 * `icon` holds an `<app-icon>` name (a file under `assets/images/svg/`, without
 * the extension). The desktop bar is text-only, so the icons surface in the
 * responsive (mobile) menu only — see FEC-1150.
 */
export const TopMenuNavigationList: SideNavigationMenuType[] = [
  {
    icon: 'apartment-building',
    key: 'project',
    title: 'Projects',
    link: '/',
    isExact: true,
    roles: [UserRoleEnum.AgencyManager, UserRoleEnum.Agent],
    permissions: [ALL_PERMISSIONS.CREATE_PROJECT, ALL_PERMISSIONS.VIEW_PROJECT],
    type: 'link',
  },
  {
    icon: 'apartment-building',
    key: 'project',
    title: 'Projects',
    link: '/',
    isExact: true,
    /* Master Agent now mirrors Developer — same Projects dropdown (Add Project /
       Add Precinct) and same urlIncludes for active-tab matching. */
    roles: [UserRoleEnum.Developer, UserRoleEnum.DeveloperViewer, UserRoleEnum.MasterAgent],
    permissions: [ALL_PERMISSIONS.CREATE_PROJECT, ALL_PERMISSIONS.VIEW_PROJECT],
    urlIncludes: ['project-detail', 'project/', 'precinct/'],
    type: 'dropdown',
  },
  {
    icon: 'menu/calendar',
    key: 'reservations',
    title: 'Reservations',
    link: '/reservations',
    isExact: false,
    /* FEC-1225 (Hina, 17 Sep): Developer Viewer no longer gets Reservations or
       Requests — "remove access to Reservation and Request functions on the top
       menu bar". Both are approval queues, and the role may approve nothing.
       The backend catalogue drops VIEW_RESERVATION / VIEW_REQUEST from the role
       in the same change, so the Permissions matrix agrees with the menu. */
    roles: [UserRoleEnum.Developer, UserRoleEnum.MasterAgent],
    permissions: [ALL_PERMISSIONS.VIEW_RESERVATION],
    type: 'link',
  },
  {
    /* Project Sales Pipeline — visible to every role; the backend scopes the rows
       to what each user is allowed to see (agents → own, agency managers → their
       assigned projects, admins → all via the "Viewing As" toggle). */
    icon: 'presentation-chart',
    key: 'sales-pipeline',
    title: 'Sales Pipeline',
    link: '/sales-pipeline',
    isExact: false,
    roles: [
      UserRoleEnum.Developer,
      UserRoleEnum.DeveloperViewer,
      UserRoleEnum.MasterAgent,
      UserRoleEnum.AgencyManager,
      UserRoleEnum.Agent,
    ],
    permissions: [],
    type: 'link',
  },
  {
    icon: 'menu/document-text',
    key: 'request',
    title: 'Requests',
    link: '/requests',
    isExact: true,
    /* FEC-1225: see Reservations above — no Developer Viewer. */
    roles: [UserRoleEnum.Developer, UserRoleEnum.MasterAgent],
    permissions: [ALL_PERMISSIONS.VIEW_REQUEST],
    type: 'link',
  },
  {
    icon: 'menu/building-4',
    key: 'agency',
    title: 'Agency',
    link: '/agency',
    isExact: false,
    roles: [UserRoleEnum.AgencyManager],
    /* Own-agency slugs, not the estate-wide CREATE_AGENCY / VIEW_AGENCY this
       used to name. `/agency` is the manager's OWN company record — they never
       reach the agencies list — so the estate-wide grants advertised access
       this entry could not deliver (FEC-1160 QA, 21 Aug: "I gave the agency
       manager all permissions, but they can still only view and edit their own
       agency"). Scoping the entry to VIEW_OWN_AGENCY makes the toggle that
       governs this tab the one an admin actually sees change something. */
    permissions: [ALL_PERMISSIONS.VIEW_OWN_AGENCY],
    type: 'link',
  },
  {
    icon: 'menu/building-4',
    key: 'agency',
    title: 'Agencies',
    link: '/agencies-dev',
    isExact: false,
    roles: [UserRoleEnum.Developer, UserRoleEnum.DeveloperViewer, UserRoleEnum.MasterAgent],
    permissions: [ALL_PERMISSIONS.CREATE_AGENCY, ALL_PERMISSIONS.VIEW_AGENCY],
    type: 'link',
  },
  {
    /* menu/user-profile is the same glyph at stroke-width 1.5 — the root
       user-profile.svg is drawn at 1, which made Users read noticeably lighter
       than its neighbours in the responsive menu (FEC-1150 QA). */
    icon: 'menu/user-profile',
    key: 'user',
    title: 'Users',
    link: '/users-dev',
    isExact: false,
    roles: [UserRoleEnum.Developer, UserRoleEnum.DeveloperViewer, UserRoleEnum.MasterAgent],
    permissions: [ALL_PERMISSIONS.CREATE_USER, ALL_PERMISSIONS.VIEW_USER],
    type: 'link',
  },
  {
    /* menu/user-profile is the same glyph at stroke-width 1.5 — the root
       user-profile.svg is drawn at 1, which made Users read noticeably lighter
       than its neighbours in the responsive menu (FEC-1150 QA). */
    icon: 'menu/user-profile',
    key: 'user',
    title: 'Users',
    link: '/users',
    isExact: false,
    roles: [UserRoleEnum.AgencyManager],
    permissions: [ALL_PERMISSIONS.CREATE_USER, ALL_PERMISSIONS.VIEW_USER],
    type: 'link',
  },
];
